import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormikProps } from 'formik'
import { useYupPhone } from 'src/hooks/useYupPhone'
import * as Yup from 'yup'

import Icon from '../../components/Icon'
import Link from '../../components/Link'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { actions, selectors } from '../../redux'
import { AdsFormReplyModalProps } from '../../components/AdsFormReplyModal'
import { Classified, ClassifiedCategory } from '../../rest/types/Classified'
import { BlockCategory } from '../../forms/AdsForm/types'
import { AdsReplyFormValues } from '../../forms/AdsReplyForm'
import { useSagaTakeEvery } from '../../hooks/useSaga'
import { civilityEnumToLabel } from '../../helpers'
import { Configuration } from '../../rest/types/Configuration'
import { UrlTransformer } from '../Transformers/ImageTransformer'

enum FormType {
  Default = 'Default',
  Adopt = 'Adopt',
  Liberal = 'Liberal',
  Carabin = 'Carabin',
}

const useAnswerClassified = ({
  classifiedId,
  fullClassified,
  category,
  configuration,
}: {
  classifiedId: string
  fullClassified: Classified
  category?: ClassifiedCategory
  configuration: Configuration
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isOpen = useSelector(selectors.modals.answerClassifiedModalIsOpen)
  const [formType, setFormType] = useState<FormType>(FormType.Default)
  const formikRef = useRef<FormikProps<AdsReplyFormValues>>(null)
  const user = useSelector(selectors.auth.user)
  const isPhoneLoaded = useYupPhone()

  const [{ data: schema }, getSchema] = useServiceRequest(
    selectors.answer.getAnswerSchema,
    actions.answer.getAnswerSchemaRequest,
    actions.answer.getAnswerSchemaReset
  )

  const [addAnswer, add] = useServiceRequest(
    selectors.answer.addAnswer,
    actions.answer.addAnswerRequest,
    actions.answer.addAnswerReset
  )

  useEffect(() => {
    getSchema()
  }, [getSchema])

  useSagaTakeEvery(
    actions.answer.addAnswerSuccess,
    useCallback(() => {
      formikRef.current?.resetForm()
    }, [formikRef])
  )

  useEffect(() => {
    switch (category?.form) {
      case BlockCategory.Liberal:
        setFormType(FormType.Liberal)
        break
      case BlockCategory.Job:
        setFormType(FormType.Carabin)
        break
      case BlockCategory.Adopt:
        setFormType(FormType.Adopt)
        break
      default:
        setFormType(FormType.Default)
    }
  }, [category])

  const props: AdsFormReplyModalProps = {
    title: t('answerAdsModalTitle'),
    onClose: () =>
      dispatch(actions.modals.setAnswerClassifiedModalIsOpen(false)),
    isOpen,
    form: {
      innerRef: formikRef,
      enableReinitialize: true,
      initialValues: {
        type: formType,
        civility: civilityEnumToLabel(user?.civility ?? ''),
        surname: user?.lastName ?? '',
        name: user?.firstName ?? '',
        email: user?.email ?? '',
        phone: '',
        file: null,
        message: '',
        ...([FormType.Liberal].includes(formType) && {
          sessionDuration: '',
          availability: {
            firstDate: '',
            secondDate: '',
            thirdDate: '',
            fourthDate: '',
            authorisationContact: false,
          },
        }),
        ...([FormType.Carabin, FormType.Liberal].includes(formType) && {
          address: {
            street: '',
            additional: '',
            city: '',
            postalCode: '',
            country: '',
          },
        }),
        ...([FormType.Carabin, FormType.Liberal, FormType.Adopt].includes(
          formType
        ) && {
          dateOfBirth: '0000-00-00',
          studyLevel: '',
          university: '',
        }),
        ...([FormType.Liberal, FormType.Adopt].includes(formType) && {
          rulesOpt: false,
        }),
        ...([FormType.Carabin, FormType.Adopt].includes(formType) && {
          uploadStudentCard: null,
        }),
        ...([FormType.Adopt].includes(formType) && {
          chartOpt: false,
        }),
      },
      validationSchema: Yup.object({
        civility: Yup.string().required(t('yup_required')),
        surname: Yup.string().required(t('yup_required')),
        name: Yup.string().required(t('yup_required')),
        email: Yup.string().email(t('yup_email')).required(t('yup_required')),
        phone: isPhoneLoaded
          ? Yup.string()
              .required(t('yup_required'))
              .phone('FR', t('yulp_error_message_valid_phone'))
          : Yup.string().required(t('yup_required')),
        address: Yup.object().when('type', {
          is: (value: any) =>
            [FormType.Liberal, FormType.Carabin].includes(value),
          then: () =>
            Yup.object({
              street: Yup.string().required(t('yup_required')),
              city: Yup.string().required(t('yup_required')),
              postalCode: Yup.string().required(t('yup_required')),
              country: Yup.string().required(t('yup_required')),
            }),
        }),
        availability: Yup.object().when('type', {
          is: (value: any) => [FormType.Liberal].includes(value),
          then: () =>
            Yup.object({
              firstDate: Yup.string().required(t('yup_required')),
              secondDate: Yup.string().required(t('yup_required')),
              thirdDate: Yup.string().required(t('yup_required')),
              fourthDate: Yup.string().required(t('yup_required')),
            }),
        }),
        studyLevel: Yup.string().when('type', {
          is: (value: any) =>
            [FormType.Liberal, FormType.Carabin, FormType.Adopt].includes(
              value
            ),
          then: () => Yup.string().required(t('yup_required')),
        }),
        university: Yup.string().when('type', {
          is: (value: any) =>
            [FormType.Liberal, FormType.Carabin, FormType.Adopt].includes(
              value
            ),
          then: () => Yup.string().required(t('yup_required')),
        }),
        dateOfBirth: Yup.string().when('type', {
          is: (value: any) =>
            [FormType.Liberal, FormType.Carabin, FormType.Adopt].includes(
              value
            ),
          then: () => Yup.string().required(t('yup_required')),
        }),
        rulesOpt: Yup.bool().when('type', {
          is: (value: any) => [FormType.Liberal].includes(value),
          then: () => Yup.bool().oneOf([true], t('yup_checkbox')),
        }),
        chartOpt: Yup.bool().when('type', {
          is: (value: any) => [FormType.Adopt].includes(value),
          then: () => Yup.bool().oneOf([true], t('yup_checkbox')),
        }),
        sessionDuration: Yup.string().when('type', {
          is: (value: any) => [FormType.Liberal].includes(value),
          then: () => Yup.string().required(t('yup_required')),
        }),
        message: Yup.string().required(t('yup_required')),
        file: Yup.mixed()
          .nullable()
          .test('isFile', t('yup_only_documents'), (value: any) => {
            if (value) {
              const supportedFormats = ['txt', 'pdf']
              return supportedFormats.includes(value.name.split('.').pop())
            }

            return true
          }),
      }),
      fields: {
        civility: {
          label: t('answerAdsCivilityField'),
          options:
            schema?.civility?.enum?.map((v: string) => ({
              label: v,
              value: v,
            })) ?? [],
        },
        surname: {
          label: t('answerAdsSurnameField'),
        },
        name: {
          label: t('answerAdsNameField'),
        },
        email: {
          label: t('answerAdsEmailField'),
        },
        phone: {
          label: t('answerAdsPhoneField'),
          placeholder: t('answerAdsPhoneFieldPlaceholder'),
          country: 'fr',
        },
        ...([FormType.Liberal, FormType.Carabin].includes(formType) && {
          address: {
            title: t('answerAdsAddressTitle'),
            street: {
              label: t('answerAdsAddressStreetField'),
            },
            additional: {
              label: t('answerAdsAdditionalField'),
            },
            city: {
              label: t('answerAdsCityField'),
            },
            postalCode: {
              label: t('answerAdsPostalCodeField'),
            },
            country: {
              label: t('answerAdsCountryField'),
            },
          },
        }),
        ...([FormType.Liberal, FormType.Carabin, FormType.Adopt].includes(
          formType
        ) && {
          dateOfBirth: {
            label: t('answerAdsdateOfBirthField'),
          },
          studyLevel: {
            label: t('answerAdsStudyLevelField'),
            placeholder: t('answerAdsStudyLevelPlaceholder'),
            options:
              schema?.schoolingLevel?.enum?.map((v: string) => ({
                label: v,
                value: v,
              })) ?? [],
          },
          university: {
            label: t('answerAdsUniversityField'),
            placeholder: t('answerAdsUniversityPlaceholder'),
            options:
              schema?.university?.enum?.map((v: string) => ({
                label: v,
                value: v,
              })) ?? [],
          },
        }),
        ...([FormType.Liberal].includes(formType) && {
          availability: {
            title: t('answerAdsAvailabilityTitle'),
            firstDate: {
              placeholder: t('answerAdsAvailabilityDatePlaceholder'),
            },
            secondDate: {
              placeholder: t('answerAdsAvailabilityDatePlaceholder'),
            },
            thirdDate: {
              placeholder: t('answerAdsAvailabilityDatePlaceholder'),
            },
            fourthDate: {
              placeholder: t('answerAdsAvailabilityDatePlaceholder'),
            },
            authorisationContact: {
              label: t('answerAdsAuthorizationContactField'),
            },
          },
          sessionDuration: {
            label: t('answerAdssessionDurationField'),
            options:
              schema?.jobDuration?.enum?.map((v: string) => ({
                label: v,
                value: v,
              })) ?? [],
          },
        }),
        ...([FormType.Adopt, FormType.Carabin].includes(formType) && {
          uploadStudentCard: {
            label: t('answerAdsUploadStudentCardField'),
            placeholder: t('answerAdsUploadStudentCardField'),
          },
        }),
        ...([FormType.Carabin].includes(formType) && {
          sessionDurationCarabin: {
            label: t('answerAdssessionDurationCarabinField'),
          },
        }),
        ...([FormType.Liberal].includes(formType) && {
          rulesOpt: {
            title: t('answerAdsRulesField'),
            label: t('answerAdsRulesField'),
            link: (
              <>
                {t('answerAdsRulesDownload')}{' '}
                <Link
                  text={t('answerAdsRulesClickHere')}
                  href={UrlTransformer(configuration.config.rulesFile)}
                  target="_blank"
                />{' '}
                <Icon
                  width={16}
                  height={16}
                  color={theme.colors.blueNormal}
                  icon="download"
                />
              </>
            ),
          },
        }),
        ...([FormType.Adopt].includes(formType) && {
          chartOpt: {
            title: t('answerAdsCharterField'),
            label: t('answerAdsCharterField'),
            link: (
              <>
                {t('answerAdsCharterDownload')}{' '}
                <Link
                  text={t('answerAdsRulesClickHere')}
                  href={UrlTransformer(configuration.config.charterFile)}
                  target="_blank"
                />{' '}
                <Icon
                  width={16}
                  height={16}
                  color={theme.colors.blueNormal}
                  icon="download"
                />
              </>
            ),
          },
        }),
        message: {
          label: t('answerAdsMessageField'),
          placeholder: t('answerAdsMessagePlaceholder'),
        },
        file: {
          label: t('answerAdsFileField'),
          placeholder: t('answerAdsFilePlaceholder'),
        },
      },
      submitButton: {
        text: t('answerAdsRulesSubmit'),
      },
      ...((addAnswer.success || addAnswer.errors) && {
        submissionValidationText: addAnswer.success
          ? t('answerAdsSubmissionValidationTextSuccess')
          : t('answerAdsSubmissionValidationTextError'),
      }),
      onSubmit: (values) => {
        add({
          ...values,
          answer_user: user?.documentId ?? user?.id,
          classified: classifiedId,
          fullClassified: fullClassified,
        })
      },
    },
  }

  return props
}

export default useAnswerClassified
